import { useState, useCallback, useRef, useMemo } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { isCompanyPreviewModalOpenedAtom } from '@src/atom/ui'
import vendorAtom from '@src/atom/vendor'
import { useHotdealChallengeTable } from './useHotdealChallengeTable'
import { useHotdealChallengeModificationModal } from './useHotdealChallengeModificationModal'
import { CompanyInfo } from '../type'

export function useChallengeListPage() {
	const modificationModalBodyRef = useRef<HTMLDivElement>(null)

	const [selectedTableItemIndex, setSelectedTableItemIndex] = useState<number>()

	const [isModificationModalOpened, setIsModificationModalOpened] = useState(false)

	const [isChallengePreviewModalOpened, setIsChallengePreviewModalOpened] = useState(false)

	const onChallengePreviewModalCanceled = useCallback(() => {
		setIsChallengePreviewModalOpened(false)
		setSelectedTableItemIndex(undefined)
	}, [])

	const onChallengePreviewOpened = useCallback((index: number | undefined) => {
		setSelectedTableItemIndex(index)
		setIsChallengePreviewModalOpened(true)
	}, [])

	const onModificationOpened = useCallback(async (index: number | undefined) => {
		setSelectedTableItemIndex(index)
		setIsModificationModalOpened(true)
		if (modificationModalBodyRef.current) {
			await new Promise((resolve) => {
				setTimeout(resolve, 100) // INFO: 모달이 열리는 시간
			})
			modificationModalBodyRef.current.scrollTo(0, 0)
		}
	}, [])

	const onModificationClosed = useCallback(() => {
		setIsModificationModalOpened(false)
		setSelectedTableItemIndex(undefined)
	}, [])

	const { pagination, isFetchingCompanyChallenges, tableItems, queryKey, selectedTableItem } =
		useHotdealChallengeTable({
			selectedTableItemIndex,
			onChallengePreviewOpened,
			onModificationOpened,
		})

	const {
		topicOptions,
		isEtcTopicModalOpened,
		onEtcTopicModalCanceled,
		selectedTopicName,
		onModificationModalCanceled,
		form,
		onFormChanged,
		hasThumbnailError,
		hasStartDateError,
		startDateErrorMessage,
		thumbnail,
		onThumbnailUploaded,
		onThumbnailRemoved,
		descriptionImages,
		onDescriptionImageUploaded,
		onDescriptionImageRemoved,
		startDate,
		onStartDateChanged,
		canDraftHotdealChallenge,
		canCreateHotdealChallenge,
		canModifyHotdealChallenge,
		onDraftClicked,
		onGuidedDraftClicked,
		onCreateClicked,
		onModifyClicked,
		isDrafting,
		isCreating,
		isModifying,
		isModificationWarnModalOpened,
		onModificationModalCloseConfirmed,
		onModificationWarnModalCanceled,
		isCardRequireModalOpened,
		onCardRequireModalCanceled,
		isAllowModalOpened,
		onAllowModalCanceled,
	} = useHotdealChallengeModificationModal({
		queryKey,
		selectedTableItem,
		onModificationClosed,
	})

	const [isCompanyPreviewModalOpened, setIsCompanyPreviewModalOpened] = useRecoilState(
		isCompanyPreviewModalOpenedAtom,
	)

	const onCompanyPreviewModalCanceled = useCallback(() => {
		setIsCompanyPreviewModalOpened(false)
	}, [setIsCompanyPreviewModalOpened])

	const vendor = useRecoilValue(vendorAtom)

	const companyInfo = useMemo<CompanyInfo>(() => {
		return {
			id: vendor.username,
			name: vendor.brandName,
			profileImage: vendor.profileImg,
			description: vendor.description,
		}
	}, [vendor])

	return {
		pagination,
		isFetchingCompanyChallenges,
		tableItems,
		isModificationModalOpened,
		topicOptions,
		isEtcTopicModalOpened,
		onEtcTopicModalCanceled,
		selectedTopicName,
		onModificationModalCanceled,
		isChallengePreviewModalOpened,
		onChallengePreviewModalCanceled,
		form,
		onFormChanged,
		hasThumbnailError,
		hasStartDateError,
		startDateErrorMessage,
		thumbnail,
		onThumbnailUploaded,
		onThumbnailRemoved,
		descriptionImages,
		onDescriptionImageUploaded,
		onDescriptionImageRemoved,
		startDate,
		onStartDateChanged,
		modificationModalBodyRef,
		selectedTableItem,
		canDraftHotdealChallenge,
		canCreateHotdealChallenge,
		canModifyHotdealChallenge,
		onDraftClicked,
		onGuidedDraftClicked,
		onCreateClicked,
		onModifyClicked,
		isDrafting,
		isCreating,
		isModifying,
		isModificationWarnModalOpened,
		onModificationModalCloseConfirmed,
		onModificationWarnModalCanceled,
		isCardRequireModalOpened,
		onCardRequireModalCanceled,
		isCompanyPreviewModalOpened,
		onCompanyPreviewModalCanceled,
		companyInfo,
		isAllowModalOpened,
		onAllowModalCanceled,
	}
}
